/**
 * Custom hook for fetching configuration data
 */

import { useState, useEffect } from 'react';
import {
  getLocalConfigurationByLocalId,
} from '../../../../../../../crud/management_api/local_configurations';
import {
  getCajasOptions,
  getLanguagesOptions,
  getMonedasOptions,
  getPaymentTypesOptions,
  getPaymentTypesByLocal,
  getTimezonesOptions,
  LanguageOption,
  TimezoneOption,
  MonedaOption,
  CajaOption,
  PaymentTypeOption,
} from '../components/utils/utils';
import { getLocalId } from '../utils';
import type { AjustesConfig } from '../utils';

interface UseAjustesDataReturn {
  isLoading: boolean;
  languages: LanguageOption[];
  timezones: TimezoneOption[];
  monedas: MonedaOption[];
  cajas: CajaOption[];
  paymentTypes: PaymentTypeOption[];
  localPaymentTypes: number[];
  config: AjustesConfig | null;
}

export const useAjustesData = (): UseAjustesDataReturn => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [timezones, setTimezones] = useState<TimezoneOption[]>([]);
  const [monedas, setMonedas] = useState<MonedaOption[]>([]);
  const [cajas, setCajas] = useState<CajaOption[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<PaymentTypeOption[]>([]);
  const [localPaymentTypes, setLocalPaymentTypes] = useState<number[]>([]);
  const [config, setConfig] = useState<AjustesConfig | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const localId = getLocalId();
        setIsLoading(true);

        const [
          configResponse,
          languagesOptions,
          timezonesOptions,
          monedasOptions,
          cajasOptions,
          paymentTypesOptions,
          localPayments,
        ] = await Promise.all([
          getLocalConfigurationByLocalId(localId),
          getLanguagesOptions(),
          getTimezonesOptions(),
          getMonedasOptions(),
          getCajasOptions(),
          getPaymentTypesOptions(),
          getPaymentTypesByLocal(localId),
        ]);

        if (configResponse && configResponse.data) {
          setConfig(configResponse.data);
        }

        setLanguages(languagesOptions || []);
        setTimezones(timezonesOptions || []);
        setMonedas(monedasOptions || []);
        setCajas(cajasOptions || []);
        setPaymentTypes(paymentTypesOptions || []);
        setLocalPaymentTypes(localPayments);
      } catch (error) {
        console.error('Error fetching local configuration:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return {
    isLoading,
    languages,
    timezones,
    monedas,
    cajas,
    paymentTypes,
    localPaymentTypes,
    config,
  };
};
